/**
 * InfoCard - Reusable card component for overview sections
 *
 * Displays a card with icon, title, and list of info items
 *
 * @example
 * ```tsx
 * <InfoCard
 *   icon={<HiOutlineEnvelope size={18} />}
 *   title="Contact"
 *   items={[
 *     { label: "Email", value: user.email, valueAsLink: { href: `mailto:${user.email}` } },
 *     { label: "Phone", value: user.phone, valueAsLink: { href: `tel:${user.phone}` } },
 *   ]}
 * />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import type { ReactElement } from "react";
import { Card } from "../../ui/Card";
import { Link } from "../../ui/Link";
import { Text } from "../../ui/Text";
import { useThemeTokens } from "@/lib/theme/tokens";
import { InfoItem, type InfoItemProps } from "../InfoItem";

export interface InfoCardItem {
  /**
   * Label for the item
   */
  label: string;
  /**
   * Value to display
   */
  value?: string | React.ReactNode;
  /**
   * Display as link
   */
  valueAsLink?: InfoItemProps["valueAsLink"];
  /**
   * Display as badge
   */
  valueAsBadge?: InfoItemProps["valueAsBadge"];
  /**
   * Icon for the item (optional, overrides card icon)
   */
  icon?: ReactElement;
}

export interface InfoCardProps {
  /**
   * Icon to display in header
   */
  icon?: ReactElement;
  /**
   * Card title
   */
  title: string;
  /**
   * List of items to display
   */
  items: InfoCardItem[];
  /**
   * Custom content to display instead of items
   */
  children?: React.ReactNode;
  /**
   * Optional action element to display in the header (e.g., edit button)
   */
  headerAction?: React.ReactNode;
}

export const InfoCard = ({ icon, title, items, children, headerAction }: InfoCardProps) => {
  const theme = useThemeTokens();

  return (
    <Card h="100%" display="flex" flexDirection="column">
      <Box p={{ base: 5, md: 6 }} flex={1} display="flex" flexDirection="column">
        <Flex align="center" justify="space-between" gap={2} mb={4}>
          <Flex align="center" gap={2}>
            {icon && (
              <Box color={theme.accent.primary} flexShrink={0}>
                {icon}
              </Box>
            )}
            <Text
              as="span"
              fontSize="sm"
              fontWeight="700"
              color={theme.text.primary}
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              {title}
            </Text>
          </Flex>
          {headerAction}
        </Flex>
        {children ? (
          <Box flex={1}>{children}</Box>
        ) : (
          <Flex direction="column" gap={3} flex={1}>
            {items.map((item, index) => (
              <Box key={index}>
                <Text
                  as="div"
                  fontSize="xs"
                  color={theme.text.tertiary}
                  mb={0.5}
                  fontWeight="600"
                >
                  {item.label}
                </Text>
                {item.valueAsLink ? (
                  <Link
                    href={item.valueAsLink.href}
                    fontSize="sm"
                    color={theme.accent.muted}
                    fontWeight="500"
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": {
                          textDecoration: "underline",
                          color: theme.accent.primary,
                        },
                      },
                    }}
                    transition="color 0.2s"
                  >
                    {item.valueAsLink.children || item.value}
                  </Link>
                ) : item.valueAsBadge ? (
                  <Box>
                    <InfoItem
                      label=""
                      valueAsBadge={item.valueAsBadge}
                      showIconBox={false}
                    />
                  </Box>
                ) : (
                  <Text as="div" fontSize="sm" color={theme.text.primary} fontWeight="500">
                    {item.value || "-"}
                  </Text>
                )}
              </Box>
            ))}
          </Flex>
        )}
      </Box>
    </Card>
  );
};

